import { upiLink } from "@luckymenu/billing-core";
import { localParts, type MySubscription, type PlatformInvoice } from "@luckymenu/core";

/**
 * The owner's view of what the restaurant pays LuckyMenu (P10-T16, P10-T17):
 * the banner to show, the next due date, and the UPI link for an invoice.
 * See docs/architecture/platform-billing.md, Owner's view.
 */

type OwnerSubscription = MySubscription["subscription"];

const DAY_MS = 24 * 60 * 60 * 1000;

/** Platform billing's dates are India's (platform-billing.md). */
const BILLING_TIME_ZONE = "Asia/Kolkata";

/** A trial banner shows in its last this many days (P10-T17). */
export const TRIAL_NOTICE_DAYS = 3;

/**
 * The banner across the staff app for the owner, or null: `pastDue` while a
 * period is unpaid (with the date the restaurant will be paused), and
 * `trialEnding` in the last three days of a trial (`daysLeft` 0 on its last
 * day). A suspended restaurant shows the paused screen instead.
 */
export type SubscriptionNotice =
	| { kind: "pastDue"; graceUntil: string | null }
	| { kind: "trialEnding"; daysLeft: number; trialEndsAt: string };

export function subscriptionNotice(
	subscription: Pick<OwnerSubscription, "status" | "trialEndsAt" | "graceUntil"> | null,
	now: Date,
): SubscriptionNotice | null {
	if (!subscription) return null;
	if (subscription.status === "past_due") {
		return { kind: "pastDue", graceUntil: subscription.graceUntil };
	}
	if (subscription.status === "trialing" && subscription.trialEndsAt) {
		const left = new Date(subscription.trialEndsAt).getTime() - now.getTime();
		if (left <= 0 || left > TRIAL_NOTICE_DAYS * DAY_MS) return null;
		// Calendar days in India, where platform billing keeps its dates: a
		// trial that ends the day after tomorrow at 08:50 has 2 days left, not 1.
		const today = localParts(now, BILLING_TIME_ZONE).date;
		const endDay = localParts(new Date(subscription.trialEndsAt), BILLING_TIME_ZONE).date;
		return {
			kind: "trialEnding",
			daysLeft: Math.round(
				(Date.parse(`${endDay}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) / DAY_MS,
			),
			trialEndsAt: subscription.trialEndsAt,
		};
	}
	return null;
}

/**
 * When the next payment is due: the earliest unpaid invoice's due date, or
 * else the day the paid period (or the trial) ends. Null with neither.
 */
export function nextDueDate(
	subscription: Pick<OwnerSubscription, "status" | "trialEndsAt" | "currentPeriodEnd"> | null,
	unpaid: Pick<PlatformInvoice, "dueDate">[],
): { kind: "invoice"; date: string } | { kind: "periodEnd" | "trialEnd"; at: string } | null {
	const first = [...unpaid].sort((a, b) => a.dueDate.localeCompare(b.dueDate))[0];
	if (first) return { kind: "invoice", date: first.dueDate };
	if (!subscription || subscription.status === "cancelled") return null;
	if (subscription.status === "trialing" && subscription.trialEndsAt) {
		return { kind: "trialEnd", at: subscription.trialEndsAt };
	}
	if (subscription.currentPeriodEnd)
		return { kind: "periodEnd", at: subscription.currentPeriodEnd };
	return null;
}

/**
 * The UPI link for paying what is still owed on an invoice to LuckyMenu, as
 * the invoice PDF's QR has it (our UPI ID, our legal name, the amount, the
 * invoice number as the note). Null without a UPI ID or with nothing owed.
 */
export function invoiceUpiLink(
	payTo: MySubscription["payTo"] | null,
	invoice: { invoiceNo: string; owedPaise: number },
): string | null {
	if (!payTo?.upiId || invoice.owedPaise <= 0) return null;
	try {
		return upiLink({
			vpa: payTo.upiId,
			payeeName: payTo.legalName,
			amountPaise: invoice.owedPaise,
			billNo: invoice.invoiceNo,
		});
	} catch {
		return null;
	}
}
